import { mulberry32, seedFrom } from "./random.js";
/**
 * Robot engineer — write the program, then run it.
 *
 * The child queues commands and only then presses run, which is what separates this from every
 * other activity here: the answer is committed before any feedback arrives. Nothing can be
 * corrected mid-move, so a child who has not thought it through finds out by watching the robot
 * walk into a wall.
 *
 * That makes **how many runs a trial took** the most informative thing recorded. A child who plans
 * runs once; a child who guesses runs three times and adjusts. Both may reach the goal, and they
 * are not the same performance. Program length against the shortest possible program is the second
 * signal — the same route can be written efficiently or clumsily.
 *
 * Neither becomes a claim about the child. Planning and learning rate are descriptive-only in the
 * Phase 0 triage; this records what happened.
 */
/** Commands, as they are stored in a program and in the event record. */
export const FORWARD = 0;
export const TURN_LEFT = 1;
export const TURN_RIGHT = 2;
/** Facings, clockwise from up. Turning is `(facing + 1) % 4` and `(facing + 3) % 4`. */
export const UP_FACING = 0;
export const RIGHT_FACING = 1;
export const DOWN_FACING = 2;
export const LEFT_FACING = 3;
/** Where the robot ends up moving forward, or null if a wall or the grid edge stops it. */
export function ahead(cell, facing, size, blocked) {
    const r = Math.floor(cell / size);
    const c = cell % size;
    let next;
    if (facing === UP_FACING)
        next = r > 0 ? cell - size : -1;
    else if (facing === DOWN_FACING)
        next = r < size - 1 ? cell + size : -1;
    else if (facing === LEFT_FACING)
        next = c > 0 ? cell - 1 : -1;
    else
        next = c < size - 1 ? cell + 1 : -1;
    if (next < 0 || blocked.has(next))
        return null;
    return next;
}
/**
 * Runs a program and reports where the robot ended up.
 *
 * A forward that would hit a wall is a NO-OP rather than an error: the robot stays put and the
 * rest of the program continues. Aborting the run on the first bad move would tell a child their
 * program was wrong at the moment it went wrong, which is feedback the commit-then-run design is
 * deliberately withholding — and it would make a program's outcome depend on where its mistake
 * was rather than on what it did.
 */
export function runProgram(puzzle, program) {
    const blocked = new Set(puzzle.blocked);
    let cell = puzzle.start;
    let facing = puzzle.facing;
    const trail = [cell];
    let bumps = 0;
    for (const command of program) {
        if (command === TURN_LEFT)
            facing = (facing + 3) % 4;
        else if (command === TURN_RIGHT)
            facing = (facing + 1) % 4;
        else {
            const next = ahead(cell, facing, puzzle.size, blocked);
            if (next === null)
                bumps++;
            else {
                cell = next;
                trail.push(cell);
            }
        }
    }
    return { cell, facing, trail, bumps };
}
export const reachedGoal = (puzzle, program) => runProgram(puzzle, program).cell === puzzle.goal;
/**
 * Shortest program that reaches the goal, by breadth-first search over (cell, facing).
 *
 * Over PAIRS, not cells: the cost of reaching a cell depends on which way the robot is pointing
 * when it gets there, so a search over cells alone would under-count the turns and report an
 * optimum no program can achieve. Returns null when the goal is unreachable.
 */
function shortestProgram(size, start, facing, goal, blocked) {
    const states = size * size * 4;
    const dist = new Array(states).fill(-1);
    const from = start * 4 + facing;
    dist[from] = 0;
    const queue = [from];
    for (let head = 0; head < queue.length; head++) {
        const state = queue[head];
        const cell = Math.floor(state / 4);
        const dir = state % 4;
        if (cell === goal)
            return dist[state];
        const next = [cell * 4 + ((dir + 1) % 4), cell * 4 + ((dir + 3) % 4)];
        const forward = ahead(cell, dir, size, blocked);
        if (forward !== null)
            next.push(forward * 4 + dir);
        for (const n of next) {
            if ((dist[n] ?? -1) >= 0)
                continue;
            dist[n] = dist[state] + 1;
            queue.push(n);
        }
    }
    return null;
}
export const ROBOT_MIN_DIFFICULTY = 1;
export const ROBOT_MAX_DIFFICULTY = 8;
/**
 * Eight levels: grid, then obstacles, then required program length.
 *
 * The grid stops growing at 6×6. Cells fall below the touch floor well before that, and it does
 * not matter — the child taps command buttons, never a cell — but a 7×7 of 30 px cells stops being
 * readable, and past six the search space makes the generator's BFS the slowest thing in the
 * package.
 */
function recipeFor(difficulty) {
    switch (difficulty) {
        case 1:
            return { size: 3, obstacles: 0, minCommands: 3 };
        case 2:
            return { size: 3, obstacles: 1, minCommands: 3 };
        case 3:
            return { size: 4, obstacles: 1, minCommands: 4 };
        case 4:
            return { size: 4, obstacles: 2, minCommands: 5 };
        case 5:
            return { size: 5, obstacles: 3, minCommands: 6 };
        case 6:
            return { size: 5, obstacles: 4, minCommands: 8 };
        case 7:
            return { size: 6, obstacles: 5, minCommands: 10 };
        default:
            return { size: 6, obstacles: 6, minCommands: 12 };
    }
}
/**
 * A puzzle for one trial.
 *
 * The generated puzzle must need at least `minCommands` — a one-move puzzle is solved by pressing
 * forward and teaches nothing about sequencing — and at most a program a child can hold in view.
 */
export function generateRobotPuzzle(task, formId, trialIndex, difficulty) {
    if (difficulty < ROBOT_MIN_DIFFICULTY || difficulty > ROBOT_MAX_DIFFICULTY) {
        throw new Error(`difficulty ${difficulty} out of range for ${task.id}`);
    }
    const { size, obstacles, minCommands } = recipeFor(difficulty);
    const cells = size * size;
    const rand = mulberry32(seedFrom(formId, trialIndex, difficulty));
    const MIN_COMMANDS = minCommands;
    /**
     * A window above the floor rather than one fixed ceiling for every level.
     *
     * With a single ceiling, the top levels would accept any puzzle from three commands upwards and
     * a 6×6 grid would routinely produce a four-command puzzle — the grid grew and the difficulty
     * did not. Four commands of headroom is enough for the search to find layouts without letting
     * an easy one through.
     */
    const MAX_COMMANDS = Math.min(MAX_PROGRAM_LENGTH, minCommands + 4);
    for (let attempt = 0; attempt < 600; attempt++) {
        const start = Math.floor(rand() * cells);
        const facing = Math.floor(rand() * 4);
        let goal = Math.floor(rand() * cells);
        if (goal === start)
            goal = (goal + 1 + Math.floor(rand() * (cells - 1))) % cells;
        const blocked = new Set();
        let guard = 0;
        while (blocked.size < obstacles && guard++ < 50) {
            const cell = Math.floor(rand() * cells);
            if (cell !== start && cell !== goal)
                blocked.add(cell);
        }
        const optimal = shortestProgram(size, start, facing, goal, blocked);
        if (optimal !== null && optimal >= MIN_COMMANDS && optimal <= MAX_COMMANDS) {
            return { size, start, facing, goal, blocked: [...blocked].sort((a, b) => a - b), optimalCommands: optimal };
        }
    }
    // Unreachable in practice: with obstacles bounded well below the cell count, a satisfying layout
    // is common. Throwing beats returning a puzzle that violates the bounds, because a trial a child
    // cannot solve would be scored against them.
    throw new Error(`no solvable robot puzzle for ${task.id} at difficulty ${difficulty} within the command bounds`);
}
/**
 * A shortest program that reaches the goal, or null if the goal is unreachable.
 *
 * The same breadth-first search as `shortestProgram`, keeping the path instead of only its length.
 * Used by the wordless demonstration, which has to PLAY a correct program rather than state that
 * one exists — and it must be a shortest one, or the tutorial teaches a detour.
 */
export function shortestSolution(puzzle) {
    const blocked = new Set(puzzle.blocked);
    const { size, start, facing, goal } = puzzle;
    const states = size * size * 4;
    const from = start * 4 + facing;
    const cameFrom = new Array(states).fill(null);
    const seen = new Array(states).fill(false);
    seen[from] = true;
    const queue = [from];
    for (let head = 0; head < queue.length; head++) {
        const state = queue[head];
        const cell = Math.floor(state / 4);
        const dir = state % 4;
        if (cell === goal) {
            const path = [];
            let cursor = state;
            while (cursor !== from) {
                const step = cameFrom[cursor];
                if (!step)
                    return null;
                path.push(step.command);
                cursor = step.state;
            }
            return path.reverse();
        }
        const moves = [
            { next: cell * 4 + ((dir + 1) % 4), command: TURN_RIGHT },
            { next: cell * 4 + ((dir + 3) % 4), command: TURN_LEFT },
        ];
        const forward = ahead(cell, dir, size, blocked);
        if (forward !== null)
            moves.push({ next: forward * 4 + dir, command: FORWARD });
        for (const { next, command } of moves) {
            if (seen[next])
                continue;
            seen[next] = true;
            cameFrom[next] = { state, command };
            queue.push(next);
        }
    }
    return null;
}
/**
 * How many runs a child gets before the trial closes.
 *
 * More than one on purpose: the whole point is that a program is committed and then observed, and
 * a single attempt would make the trial a coin toss rather than a chance to reason about what
 * happened. Bounded so that "run until it works" is not a strategy — which is exactly what the
 * run count is there to distinguish.
 */
export const MAX_RUNS_PER_PUZZLE = 3;
/** Longest program a child may queue. Bounded by what fits on screen at a readable size. */
/**
 * Slots the child may queue.
 *
 * Raised from twelve when the ladder reached eight levels: the top level's shortest solution can be
 * twelve commands, and a queue capped at exactly the optimum leaves no room to be wrong, which
 * turns a planning game into a game with one legal answer per slot.
 */
export const MAX_PROGRAM_LENGTH = 18;
//# sourceMappingURL=robot.js.map